import { OfflineDatabase } from "../database";
import { CachedPreOrder, STORES } from "../schema";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";

const EXPIRY_DAYS = 30;

export class PreOrderStore {
  constructor(private db: OfflineDatabase) {}

  // Generate cache key
  private makeKey(date: string, preOrderId: string): string {
    return `${date}:${preOrderId}`;
  }

  // Get single pre-order
  async get(date: string, preOrderId: string): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (entry) {
      // Update access time for LRU
      entry.accessedAt = Date.now();
      await this.db.set(STORES.PRE_ORDERS, entry);

      // Filter out if soft-deleted
      if (entry.preOrder.deleted_at) {
        return null;
      }

      // Filter out soft-deleted flows
      if (entry.preOrder.flows) {
        entry.preOrder.flows = entry.preOrder.flows.filter(
          (flow) => !flow.deleted_at,
        );
      }

      return entry.preOrder;
    }

    return null;
  }

  // Get all pre-orders for a date
  async getByDate(date: string): Promise<PreOrder[]> {
    const entries = await this.db.queryByIndex<CachedPreOrder>(
      STORES.PRE_ORDERS,
      "date",
      date,
    );

    // Update access times
    const now = Date.now();
    await Promise.all(
      entries.map((entry) => {
        entry.accessedAt = now;
        return this.db.set(STORES.PRE_ORDERS, entry);
      }),
    );

    // Filter out soft-deleted pre-orders and their soft-deleted flows
    return entries
      .map((e) => e.preOrder)
      .filter((po) => !po.deleted_at)
      .map((po) => ({
        ...po,
        flows: po.flows?.filter((flow) => !flow.deleted_at),
      }));
  }

  // Cache a pre-order
  async set(date: string, preOrder: PreOrder): Promise<void> {
    const now = Date.now();
    const entry: CachedPreOrder = {
      key: this.makeKey(date, preOrder.id),
      date,
      preOrderId: preOrder.id,
      preOrder,
      lastSyncedAt: now,
      serverUpdatedAt: preOrder.updated_at,
      accessedAt: now,
      createdAt: now,
      expiresAt: now + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    };

    await this.db.set(STORES.PRE_ORDERS, entry);
  }

  // Cache multiple pre-orders
  async setMany(date: string, preOrders: PreOrder[]): Promise<void> {
    await Promise.all(preOrders.map((po) => this.set(date, po)));
    await this.db.enforceQuota();
  }

  // Update a flow within a pre-order
  async updateFlow(
    date: string,
    preOrderId: string,
    flowId: string,
    updates: Partial<PreOrderFlow>,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    // Update the flow
    const flowIndex =
      entry.preOrder.flows?.findIndex((f) => f.id === flowId) ?? -1;
    if (flowIndex >= 0 && entry.preOrder.flows) {
      entry.preOrder.flows[flowIndex] = {
        ...entry.preOrder.flows[flowIndex],
        ...updates,
      };
    }

    entry.accessedAt = Date.now();
    await this.db.set(STORES.PRE_ORDERS, entry);

    return entry.preOrder;
  }

  // Update multiple flows within a pre-order atomically (single read-modify-write)
  // This avoids race conditions when updating multiple flows in parallel
  async updateFlows(
    date: string,
    preOrderId: string,
    flowUpdates: Array<{ flowId: string; updates: Partial<PreOrderFlow> }>,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    // Update all flows in a single pass
    for (const { flowId, updates } of flowUpdates) {
      const flowIndex =
        entry.preOrder.flows?.findIndex((f) => f.id === flowId) ?? -1;
      if (flowIndex >= 0 && entry.preOrder.flows) {
        entry.preOrder.flows[flowIndex] = {
          ...entry.preOrder.flows[flowIndex],
          ...updates,
        };
      }
    }

    entry.accessedAt = Date.now();
    await this.db.set(STORES.PRE_ORDERS, entry);

    return entry.preOrder;
  }

  // Add a new flow to a pre-order
  async addFlow(
    date: string,
    preOrderId: string,
    flow: PreOrderFlow,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    if (!entry.preOrder.flows) {
      entry.preOrder.flows = [];
    }
    entry.preOrder.flows.push(flow);
    entry.accessedAt = Date.now();

    await this.db.set(STORES.PRE_ORDERS, entry);
    return entry.preOrder;
  }

  // Remove a flow from a pre-order
  async removeFlow(
    date: string,
    preOrderId: string,
    flowId: string,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    entry.preOrder.flows = entry.preOrder.flows?.filter((f) => f.id !== flowId);
    entry.accessedAt = Date.now();

    await this.db.set(STORES.PRE_ORDERS, entry);
    return entry.preOrder;
  }

  // Atomic batch update: pre-order + flows in single operation (prevents race conditions)
  async updatePreOrderAndFlows(
    date: string,
    preOrderId: string,
    preOrderUpdates: Partial<PreOrder>,
    flowUpdates: Array<{ flowId: string; updates: Partial<PreOrderFlow> }>,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    // Update pre-order fields
    entry.preOrder = {
      ...entry.preOrder,
      ...preOrderUpdates,
    };

    // Update flows
    for (const { flowId, updates } of flowUpdates) {
      const flowIndex =
        entry.preOrder.flows?.findIndex((f) => f.id === flowId) ?? -1;
      if (flowIndex >= 0 && entry.preOrder.flows) {
        entry.preOrder.flows[flowIndex] = {
          ...entry.preOrder.flows[flowIndex],
          ...updates,
        };
      }
    }

    entry.accessedAt = Date.now();
    await this.db.set(STORES.PRE_ORDERS, entry);
    return entry.preOrder;
  }

  // Update pre-order fields (status, partner_id, etc)
  async updatePreOrder(
    date: string,
    preOrderId: string,
    updates: Partial<PreOrder>,
  ): Promise<PreOrder | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);

    if (!entry) return null;

    // Update the pre-order fields (preserve flows)
    entry.preOrder = {
      ...entry.preOrder,
      ...updates,
      flows: entry.preOrder.flows, // Don't overwrite flows
    };
    entry.accessedAt = Date.now();

    await this.db.set(STORES.PRE_ORDERS, entry);
    return entry.preOrder;
  }

  // Get server updated_at for conflict detection
  async getServerUpdatedAt(
    date: string,
    preOrderId: string,
  ): Promise<string | null> {
    const key = this.makeKey(date, preOrderId);
    const entry = await this.db.get<CachedPreOrder>(STORES.PRE_ORDERS, key);
    return entry?.serverUpdatedAt ?? null;
  }

  // Get first cached entry for a pre-order (regardless of date)
  // Useful when we don't know the date (e.g., in background sync)
  async getFirst(preOrderId: string): Promise<CachedPreOrder | null> {
    const entries = await this.db.queryByIndex<CachedPreOrder>(
      STORES.PRE_ORDERS,
      "preOrderId",
      preOrderId,
    );
    return entries[0] || null;
  }

  // Delete a cached pre-order
  async delete(date: string, preOrderId: string): Promise<void> {
    const key = this.makeKey(date, preOrderId);
    await this.db.delete(STORES.PRE_ORDERS, key);
  }

  // Clear all cached data (for logout/reset)
  async clear(): Promise<void> {
    const entries = await this.db.getAll<CachedPreOrder>(STORES.PRE_ORDERS);
    await Promise.all(
      entries.map((e) => this.db.delete(STORES.PRE_ORDERS, e.key)),
    );
  }

  // Clear all cached data for a specific date
  async clearByDate(date: string): Promise<void> {
    const entries = await this.db.queryByIndex<CachedPreOrder>(
      STORES.PRE_ORDERS,
      "date",
      date,
    );
    await Promise.all(
      entries.map((e) => this.db.delete(STORES.PRE_ORDERS, e.key)),
    );
  }
}
